import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import OtpVerifyBlock from '@/components/OtpVerifyBlock';
import { extraApi } from '@/lib/extraApi';

/**
 * Public "Get Free Quote" lead-capture dialog (shared by Landing + all site pages).
 * Submits to POST /api/quote-requests; respects the admin OTP toggle.
 */
export default function QuoteDialog({ open, onClose, t, initialKw }) {
    const empty = { name: '', mobile: '', email: '', city: '', interest_kw: 3, system_type: 'ongrid', property_type: 'home', message: '' };
    const [f, setF] = useState(empty);
    const [otpRequired, setOtpRequired] = useState(false);
    const [otpToken, setOtpToken] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && initialKw) setF(prev => ({ ...prev, interest_kw: initialKw }));
    }, [open, initialKw]);
    useEffect(() => {
        if (!open) return;
        setOtpToken('');
        extraApi.publicLeadOtpRequired()
            .then(r => setOtpRequired(!!r?.public_lead_otp_required))
            .catch(() => setOtpRequired(false));
    }, [open]);

    const submit = async () => {
        if (!f.name || !f.mobile) { toast.error('Name and mobile required'); return; }
        if (otpRequired && !otpToken) { toast.error('कृपया पहले mobile OTP verify करें'); return; }
        setBusy(true);
        try {
            await extraApi.postQuoteRequest({ ...f, interest_kw: Number(f.interest_kw) || null, otp_token: otpToken || undefined });
            toast.success(t('quote_success'));
            onClose();
            setF(empty);
            setOtpToken('');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit'); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={o => !o && onClose()}>
            <DialogContent className='max-w-lg' data-testid='quote-dialog'>
                <DialogHeader><DialogTitle>{t('get_free_quote')}</DialogTitle></DialogHeader>
                <div className='grid grid-cols-2 gap-3'>
                    <div className='space-y-1 col-span-2'><Label>{t('quote_your_name')}</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid='quote-name-input' /></div>
                    <div className='space-y-1'><Label>{t('quote_your_mobile')}</Label><Input value={f.mobile} onChange={e => setF({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid='quote-mobile-input' /></div>
                    <div className='space-y-1'><Label>{t('email')}</Label><Input type='email' value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid='quote-email-input' /></div>
                    <div className='space-y-1'><Label>{t('city')}</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid='quote-city-input' /></div>
                    <div className='space-y-1'><Label>{t('quote_capacity')}</Label><Input type='number' step='0.5' value={f.interest_kw} onChange={e => setF({ ...f, interest_kw: e.target.value })} data-testid='quote-kw-input' /></div>
                    <div className='space-y-1'><Label>{t('quote_type')}</Label>
                        <Select value={f.system_type} onValueChange={v => setF({ ...f, system_type: v })}>
                            <SelectTrigger data-testid='quote-type-select'><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value='ongrid'>{t('ongrid_title')}</SelectItem>
                                <SelectItem value='offgrid'>{t('offgrid_title')}</SelectItem>
                                <SelectItem value='hybrid'>{t('hybrid_title')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='space-y-1'><Label>{t('quote_property')}</Label>
                        <Select value={f.property_type} onValueChange={v => setF({ ...f, property_type: v })}>
                            <SelectTrigger data-testid='quote-property-select'><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value='home'>{t('landing_homes')}</SelectItem>
                                <SelectItem value='society'>{t('landing_society')}</SelectItem>
                                <SelectItem value='commercial'>{t('landing_commercial')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='space-y-1 col-span-2'><Label>{t('quote_message')}</Label><Textarea rows={2} value={f.message} onChange={e => setF({ ...f, message: e.target.value })} data-testid='quote-message-input' /></div>
                    <OtpVerifyBlock required={otpRequired} mobile={f.mobile} purpose='public_lead' onVerified={setOtpToken} testId='quote-otp' />
                </div>
                <DialogFooter>
                    <Button variant='outline' onClick={onClose} data-testid='quote-cancel-button'>{t('cancel')}</Button>
                    <Button className='bg-accent hover:bg-accent/90 text-accent-foreground' onClick={submit} disabled={busy || (otpRequired && !otpToken)} data-testid='quote-submit-button'>{t('quote_submit')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
